import React from 'react';
import GenericDialogue from 'src/components/Dialogue/Dialogue';
import Button from '../../components/Button/Button';
import FontAwesomeIcon from '../Icon/FontAwesomeIcon';
import {
  EventType,
  EventTypeStyle,
  getEventTypeStyle,
  getProjectColor,
} from 'src/utils/BigCalendarColourGeneratorUtils';
import CategoryPill from 'src/components/CategoryPill/CategoryPill';
import { MobileAppContent } from 'src/components/MobileAppCallToAction/MobileAppCallToAction';
import Avatar from 'src/components/Avatar';
import { useSelector } from 'react-redux';
import StatusIcon from 'src/components/StatusIcon/StatusIcon';
import {
  ICalendarEvent,
  ICalendarEventData,
  SERVICE_REQUEST_EVENT_TYPE,
  WALKTHROUGH_EVENT_TYPE,
} from 'src/pages/Calendar/types';
import './event-details-dialogue.scss';
import { useBranding } from 'src/branding/BrandingContextProvider';

const WalkthroughActions = () => (
  <div className="event-walkthrough-actions">
    <div className="mobile-cta-container">
      <MobileAppContent
        header="Complete walkthroughs on mobile"
        className="modal-context"
      />
    </div>
  </div>
);

interface IEventDetailsHeaderProps {
  originalEvent: ICalendarEventData;
  eventTypeString: string;
  eventType: EventType;
}

const EventDetailsHeader = ({
  originalEvent,
  eventTypeString,
  eventType,
}: IEventDetailsHeaderProps) => {
  const eventStyle: EventTypeStyle = getEventTypeStyle(eventType);

  return (
    <div className="event-details-header">
      <CategoryPill
        text={eventTypeString}
        color={eventStyle.backgroundColor}
        textColor={eventStyle.textColor}
        eventType={eventType}
      />
      {originalEvent.propertyName && (
        <CategoryPill
          text={originalEvent.propertyName}
          color={getProjectColor(originalEvent.propertyName)}
          textColor="white" // Properties will use white text like in the legend.
        />
      )}
    </div>
  );
};

interface IServiceProviderCardProps {
  companyName?: string;
  name?: string;
  email?: string;
  phone?: string;
  originalEvent?: ICalendarEventData;
}

const ServiceProviderCard = ({
  companyName,
  name,
  email,
  phone,
  originalEvent,
}: IServiceProviderCardProps) => {
  const serviceRequest: any = useSelector((state: any) => state.serviceRequest);

  if (!name || (typeof name === 'string' && name.trim() === '')) {
    return (
      <div className="service-provider-card">
        <div className="provider-header">
          <div className="avatar-header">
            <Avatar url={null} badge="service-provider" size="medium" />
            <div className="provider-info">
              <h3>No Service Provider Assigned</h3>
              <div className="provider-company">
                Please assign a service provider to this request
              </div>
            </div>
          </div>
          <div className="contact-item">
            <div className="contact-value status-unassigned">
              <StatusIcon
                status="unassigned"
                showLabel={true}
                size="small"
                className="provider-status"
              />
            </div>
          </div>
        </div>
        <div className="contact-call-to-action">
          <div className="contact-item-call-to-action">
            <div className="contact-label-call-to-action">Action Needed</div>
            <div className="contact-value-call-to-action">
              Click "View Service Request" below to open this request and assign
              a service provider.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="service-provider-card">
      <div className="provider-header">
        <div className="avatar-header">
          <Avatar
            url={serviceRequest?.serviceProvider?.profileImage}
            badge="service-provider"
            size="medium"
          />
          <div className="provider-info">
            <h3>{name}</h3>
            <div className="provider-company">{companyName}</div>
          </div>
        </div>
        <div className="contact-item">
          <div className="contact-value status-assigned">
            <StatusIcon
              status={originalEvent?.status}
              showLabel={true}
              size="small"
              className="provider-status"
            />
          </div>
        </div>
      </div>
      <div className="contact-grid">
        <div className="contact-item">
          <div className="contact-label">Phone</div>
          <div className="contact-value">{phone}</div>
        </div>
        <div className="contact-item">
          <div className="contact-label">Email</div>
          <div className="contact-value">{email}</div>
        </div>
      </div>
    </div>
  );
};

interface IEventInformationProps {
  originalEvent: ICalendarEventData;
  formatDateTime: (date: Date) => string;
  getUnitNumber: () => number | string;
}

const EventInformation = ({
  originalEvent,
  formatDateTime,
  getUnitNumber,
}: IEventInformationProps) => {
  const { branding } = useBranding();

  // Check if service provider is assigned.
  const hasServiceProvider =
    originalEvent.serviceProviderName ||
    originalEvent.serviceProviderCompanyName ||
    originalEvent.serviceProviderEmail ||
    originalEvent.serviceProviderPhone;

  return (
    <div className="event-info-section">
      <h3>Event Information</h3>
      <div className="event-info-grid">
        <div className="event-info-item">
          <div className="event-info-label">Property</div>
          <div className="event-info-value">
            {originalEvent.propertyName || 'N/A'}
          </div>
        </div>
        <div className="event-info-item">
          <div className="event-info-label">Unit</div>
          <div className="event-info-value">{getUnitNumber()}</div>
        </div>
        <div className="event-info-item">
          <div className="event-info-label">Type</div>
          <div className="event-info-value">
            {originalEvent.juniperEventType === WALKTHROUGH_EVENT_TYPE
              ? 'Walkthrough'
              : 'Service Request'}
          </div>
        </div>
        <div className="event-info-item">
          <div className="event-info-label">Date and Time</div>
          <div className="event-info-value">
            {formatDateTime(new Date(originalEvent.eventDateTime))}
          </div>
        </div>
        {originalEvent.juniperEventType === WALKTHROUGH_EVENT_TYPE && (
          <div className="event-info-item">
            <div className="event-info-label">Status</div>
            <div className="event-info-item">
              <StatusIcon
                status={originalEvent?.status}
                showLabel={true}
                size="small"
                className="provider-status"
              />
            </div>
          </div>
        )}
        <div className="event-info-item">
          <div className="event-info-label">{branding.text.homeOwner}</div>
          <div className="event-info-value">
            {originalEvent.homeOwner || 'N/A'}
          </div>
        </div>
        {/* Show Days Since Submitted when available (typically for service requests). */}
        {originalEvent.daysSinceSubmitted !== null &&
          originalEvent.daysSinceSubmitted !== undefined && (
            <div className="event-info-item">
              <div className="event-info-label">Days Since Submitted</div>
              <div className="event-info-value">
                {originalEvent.daysSinceSubmitted} days
              </div>
            </div>
          )}
      </div>
      {hasServiceProvider && (
        <div className="service-provider-section">
          <h4>Assigned Service Provider</h4>
          <ServiceProviderCard
            companyName={originalEvent.serviceProviderCompanyName}
            name={originalEvent.serviceProviderName}
            email={originalEvent.serviceProviderEmail}
            phone={originalEvent.serviceProviderPhone}
            originalEvent={originalEvent}
          />
        </div>
      )}
    </div>
  );
};

interface IServiceRequestDetailsProps {
  originalEvent: ICalendarEventData;
  hasValidUrl: () => string | boolean;
  getEventUrl: () => string;
  getViewButtonLabel: () => string;
}

const ServiceRequestDetails = ({
  originalEvent,
  hasValidUrl,
  getEventUrl,
  getViewButtonLabel,
}: IServiceRequestDetailsProps) => (
  <div className="event-info-section">
    <h3>Service Request Details</h3>
    <div className="event-info-grid">
      <div className="event-info-item">
        <div className="event-info-label">Service Request Ticket Number</div>
        <div className="event-info-value ticket-number">
          {originalEvent.ticketNumber}
        </div>
      </div>
      <div className="event-info-item">
        <div className="event-info-label">Description</div>
        <div className="event-info-value">
          {originalEvent.description || originalEvent.name || 'N/A'}
        </div>
      </div>
    </div>
    {hasValidUrl() && (
      <EventActions
        getEventUrl={getEventUrl}
        getViewButtonLabel={getViewButtonLabel}
      />
    )}
  </div>
);

interface IGetActionsProps {
  getEventUrl: () => string;
  getViewButtonLabel: () => string;
}

const EventActions = ({
  getEventUrl,
  getViewButtonLabel,
}: IGetActionsProps) => (
  <div className="event-actions">
    <a href={getEventUrl()} target="_blank" rel="noopener noreferrer">
      <Button
        color="teal"
        label={getViewButtonLabel()}
        icon={
          <FontAwesomeIcon glyph="external-link" weight="light" size="small" />
        }
        size="small"
      />
    </a>
  </div>
);

interface IEventCreatedAtProps {
  originalEvent: ICalendarEventData;
}

const EventCreatedAt = ({ originalEvent }: IEventCreatedAtProps) => (
  <div className="event-created-at">
    Created: {new Date(originalEvent.createdAt).toLocaleString()}
  </div>
);

interface IEventDetailsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  event: ICalendarEvent;
}

const EventDetailsDialog = ({
  isOpen,
  onClose,
  event,
}: IEventDetailsDialogProps) => {
  if (!event || !event.originalEvent) return null;

  const originalEvent = event.originalEvent;

  const formatDateTime = (date: Date): string =>
    date.toLocaleString(undefined, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  const getEventTypeString = () => {
    if (originalEvent.juniperEventType === WALKTHROUGH_EVENT_TYPE)
      return 'Walkthrough';
    if (originalEvent.juniperEventType === SERVICE_REQUEST_EVENT_TYPE)
      return 'Service Request';
    return 'Event';
  };

  const getEventType = (): EventType => {
    if (originalEvent.juniperEventType === WALKTHROUGH_EVENT_TYPE)
      return 'walkthrough';
    if (originalEvent.juniperEventType === SERVICE_REQUEST_EVENT_TYPE)
      return 'service';
    return 'service';
  };

  const getEventUrl = (): string => {
    const baseUrl: string = window.location.origin;

    if (
      originalEvent.juniperEventType === WALKTHROUGH_EVENT_TYPE &&
      originalEvent.walkthroughId &&
      originalEvent.suiteId
    ) {
      return `${baseUrl}/app/suites/${originalEvent.suiteId}&${originalEvent.walkthroughId}`;
    } else if (
      originalEvent.juniperEventType === SERVICE_REQUEST_EVENT_TYPE &&
      originalEvent.serviceRequestId
    ) {
      // Keep existing service request URL format unchanged
      return `${baseUrl}/app/service-requests/${originalEvent.serviceRequestId}`;
    }
    return '#';
  };

  const getViewButtonLabel = (): string => {
    if (originalEvent.juniperEventType === WALKTHROUGH_EVENT_TYPE)
      return 'View Walkthrough';
    if (originalEvent.juniperEventType === SERVICE_REQUEST_EVENT_TYPE)
      return 'View Service Request';
    return 'View Event';
  };

  const getUnitNumber = () => originalEvent.number || 'Unknown Unit';

  const hasValidUrl = (): string | boolean => {
    const url: string = getEventUrl();
    if (url === '#') return false;

    // For walkthroughs, ensure both the suiteId and walkthroughId are present.
    if (originalEvent.juniperEventType === WALKTHROUGH_EVENT_TYPE) {
      return originalEvent.suiteId && originalEvent.walkthroughId;
    }

    // For service requests, ensure serviceRequestId is present
    if (originalEvent.juniperEventType === SERVICE_REQUEST_EVENT_TYPE) {
      return originalEvent.serviceRequestId;
    }

    return false;
  };

  return (
    <GenericDialogue
      isOpen={isOpen}
      onClose={onClose}
      title={originalEvent.name || originalEvent.description || 'Event Details'}
      description={`Details for this ${getEventTypeString().toLowerCase()}`}
    >
      <div className="event-details">
        <EventDetailsHeader
          originalEvent={originalEvent}
          eventTypeString={getEventTypeString()}
          eventType={getEventType()}
        />
        <EventInformation
          originalEvent={originalEvent}
          formatDateTime={formatDateTime}
          getUnitNumber={getUnitNumber}
        />
        {/* Show service request details for service requests. */}
        {originalEvent.juniperEventType === SERVICE_REQUEST_EVENT_TYPE && (
          <ServiceRequestDetails
            originalEvent={originalEvent}
            hasValidUrl={hasValidUrl}
            getEventUrl={getEventUrl}
            getViewButtonLabel={getViewButtonLabel}
          />
        )}
        {/* Show mobile CTA for walkthroughs. */}
        {originalEvent.juniperEventType === WALKTHROUGH_EVENT_TYPE && (
          <WalkthroughActions />
        )}
        <EventCreatedAt originalEvent={originalEvent} />
      </div>
    </GenericDialogue>
  );
};

export default EventDetailsDialog;
